/**
 * Modal "Registrar saldo banco al cierre" — Sprint 18 P16.
 *
 * El tesorero ingresa el saldo del banco al 31 de diciembre del año
 * cerrado (según extracto bancario). Editable: si había valor previo,
 * el modal precarga y permite sobreescribir.
 */
import { useState, type FormEvent } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api/client';
import { registrarSaldoFinalBanco } from '../../lib/api/cierres-anuales';

interface ModalRegistrarSaldoFinalBancoProps {
  /**
   * Solo necesita identidad + año + saldo actual. Lo satisface tanto
   * `CierreAnualRead` como `CuadreAnualItem` (tipado estructural), así
   * la tabla del cuadre puede abrir este modal sin convertir tipos.
   */
  cierre: { id: string; anio: number; saldo_final_banco: number | null };
  onCerrar: () => void;
  onGuardado: () => void;
}

export function ModalRegistrarSaldoFinalBanco({
  cierre,
  onCerrar,
  onGuardado,
}: ModalRegistrarSaldoFinalBancoProps) {
  const [saldoStr, setSaldoStr] = useState(
    cierre.saldo_final_banco !== null ? String(cierre.saldo_final_banco) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saldoNum = parseInt(saldoStr.replace(/\D/g, ''), 10);
  const signo = saldoStr.trim().startsWith('-') ? -1 : 1;
  const valido = saldoStr.trim().length > 0 && !isNaN(saldoNum);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await registrarSaldoFinalBanco(cierre.id, {
        saldo_final_banco: saldoNum * signo,
      });
      onGuardado();
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.body === 'object' &&
        err.body !== null &&
        'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : 'Error de red al guardar el saldo.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const esEdicion = cierre.saldo_final_banco !== null;

  return (
    <Modal
      title={`Saldo banco al cierre ${cierre.anio}`}
      onClose={onCerrar}
      footer={
        <>
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-saldo-final-banco"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : esEdicion ? 'Actualizar' : 'Registrar'}
          </Button>
        </>
      }
    >
      <form
        id="form-saldo-final-banco"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <p className="text-sm text-text-secondary">
          Ingresa el saldo del banco al 31 de diciembre del año{' '}
          <span className="text-text-primary font-medium">{cierre.anio}</span>{' '}
          según el extracto bancario. Esto permite cuadrar el flujo de caja
          anual del barrio con la realidad del banco.
        </p>
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Saldo CLP <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={saldoStr}
            onChange={(e) => setSaldoStr(e.target.value)}
            placeholder="Ej. 1500000 (o -100000 si hubo sobregiro)"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
            autoFocus
          />
          <span className="text-xs text-text-muted block mt-1">
            Acepta cero y números negativos (sobregiro). Sin puntos ni
            separadores.
          </span>
        </label>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
